// src/services/users.ts

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  Unsubscribe,
  deleteDoc,
} from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import type { AppUser, UserProfile, UserRelationship, SocialRelationshipStatus, NotificationActor } from '../types';
import { NotificationType, View } from '../types';
import { xpProgress } from '../utils/xp';

import { Capacitor } from '@capacitor/core';
import { auth } from '../lib/firebase';
import { ensureJsAuthFromNative } from './authBridge';
import { createNotification } from './notifications';

async function ensureAuthedForWrite(label: string) {
  try {
    if (auth.currentUser?.uid) return;

    const isNative = (() => {
      try {
        return Capacitor.isNativePlatform();
      } catch {
        return false;
      }
    })();

    if (isNative) {
      await Promise.race([
        ensureJsAuthFromNative(),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ]);
      if (auth.currentUser?.uid) return;
    }

    // Web and native safety net: relationship/presence listeners can mount one
    // render before Firebase has hydrated request.auth, which looks exactly like
    // a rules problem in the console. Give auth a tiny window to settle.
    const startedAt = Date.now();
    while (!auth.currentUser?.uid && Date.now() - startedAt < 2500) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  } catch (e) {
    console.warn(`[users] ensureAuthedForWrite(${label}) failed`, e);
  }
}

function safeString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function safeNumber(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function stripUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {};
  Object.keys(obj || {}).forEach((key) => {
    if ((obj as any)[key] !== undefined) out[key] = (obj as any)[key];
  });
  return out as Partial<T>;
}


function normalizeSavedSpotsForCompare(input: any): any[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: any[] = [];

  input.forEach((raw, idx) => {
    const lat = Number(raw?.lat ?? raw?.position?.lat);
    const lng = Number(raw?.lng ?? raw?.lon ?? raw?.position?.lng ?? raw?.position?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const kindRaw = String(
      raw?.kind ??
        raw?.type ??
        raw?.category ??
        raw?.spotType ??
        'favorite',
    )
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

    const kind =
      kindRaw === 'launch' ||
      kindRaw === 'boat' ||
      kindRaw === 'ramp' ||
      kindRaw === 'boat_launch'
        ? 'launch'
        : kindRaw === 'sunset' || kindRaw === 'view' || kindRaw === 'scenic'
          ? 'sunset'
          : kindRaw === 'fish' ||
              kindRaw === 'fishing' ||
              kindRaw === 'fishspot' ||
              kindRaw === 'fish_spot' ||
              kindRaw === 'fish-spot'
            ? 'fish'
            : kindRaw === 'food' ||
                kindRaw === 'restaurant' ||
                kindRaw === 'eat' ||
                kindRaw === 'meal' ||
                kindRaw === 'foodspot' ||
                kindRaw === 'food_spot' ||
                kindRaw === 'food-spot'
              ? 'food'
              : 'favorite';
    const id = String(raw?.id || `saved_${idx}`).trim();
    const name = String(raw?.name || raw?.title || kind).trim();
    const note = String(raw?.note || '').trim();
    const createdAt = Number(
      typeof raw?.createdAt?.toMillis === 'function'
        ? raw.createdAt.toMillis()
        : raw?.createdAt?.seconds
          ? raw.createdAt.seconds * 1000
          : raw?.createdAt ?? raw?.savedAt ?? raw?.timestamp ?? 0,
    ) || 0;

    const item = {
      id,
      name,
      kind,
      lat: Number(lat.toFixed(6)),
      lng: Number(lng.toFixed(6)),
      note,
      createdAt,
    };

    const key = [kind, name.toLowerCase(), item.lat, item.lng].join('|') || id;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });

  return out.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
}

function serializeSavedSpotsForCompare(input: any): string {
  return JSON.stringify(normalizeSavedSpotsForCompare(input));
}

function normalizeHandle(value: unknown): string {
  if (typeof value !== 'string') return '';

  return value
    .trim()
    .toLowerCase()
    .replace(/^@+/, '')
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9_.-]/g, '')
    .slice(0, 32);
}

function pickBestDisplayName(data: any): string {
  return (
    String(
      data?.displayName ||
      data?.name ||
      data?.username ||
      data?.handle ||
      data?.email?.split?.('@')?.[0] ||
      'Angler',
    ).trim() || 'Angler'
  );
}

function deriveCanonicalHandle(uid: string, data: any): string {
  const direct =
    normalizeHandle(data?.username) ||
    normalizeHandle(data?.handle) ||
    normalizeHandle(data?.usernameLower) ||
    normalizeHandle(data?.handleLower);

  if (direct) return direct;

  const fromName =
    normalizeHandle(data?.displayName) ||
    normalizeHandle(data?.name) ||
    normalizeHandle(data?.email?.split?.('@')?.[0]);

  if (fromName && fromName.length >= 2) return fromName;

  return `angler-${String(uid || '').trim().slice(0, 8).toLowerCase()}`;
}

function normalizeProfileLikeShape(uid: string, raw: any) {
  const displayName = pickBestDisplayName(raw);
  const canonicalHandle = deriveCanonicalHandle(uid, raw);

  return {
    ...raw,
    uid,
    displayName,
    name: String(raw?.name || displayName).trim() || displayName,
    username: String(raw?.username || canonicalHandle).trim() || canonicalHandle,
    usernameLower: canonicalHandle,
    handle: String(raw?.handle || canonicalHandle).trim() || canonicalHandle,
    handleLower: canonicalHandle,
  };
}

function pickProfileAvatar(data: any): string {
  return safeString(
    data?.avatar ||
      data?.avatarUrl ||
      data?.avatarURL ||
      data?.photoURL ||
      data?.photoUrl ||
      data?.profilePhotoUrl ||
      data?.imageUrl ||
      '',
    '',
  );
}


function normalizeAccessStatus(value: unknown): 'active' | 'waitlisted' | 'blocked' | undefined {
  const status = String(value ?? '').trim().toLowerCase();

  if (!status) return undefined;

  if (['active', 'member', 'approved'].includes(status)) return 'active';
  if (['waitlisted', 'waitlist', 'pending'].includes(status)) return 'waitlisted';
  if (['blocked', 'banned', 'disabled'].includes(status)) return 'blocked';

  return undefined;
}

function buildUserFromDoc(uid: string, data: any): AppUser {
  const normalized = normalizeProfileLikeShape(uid, data);

  const xp = safeNumber(normalized?.xp ?? normalized?.points ?? 0, 0);
  const info = xpProgress(xp);

  return {
    uid,
    id: uid,
    name: safeString(normalized?.name, 'Angler'),
    displayName: safeString(normalized?.displayName, safeString(normalized?.name, 'Angler')),
    username: safeString(normalized?.username, ''),
    usernameLower: safeString(normalized?.usernameLower, ''),
    handle: safeString(normalized?.handle, ''),
    handleLower: safeString(normalized?.handleLower, ''),
    email: safeString(normalized?.email, ''),
    accountType: normalized?.accountType,
    authProviders: Array.isArray(normalized?.authProviders)
      ? normalized.authProviders.map(String).filter(Boolean)
      : undefined,
    accessStatus: normalizeAccessStatus(
      normalized?.accessStatus ?? normalized?.status,
    ),
    waitlistNumber:
      safeNumber(normalized?.waitlistNumber, 0) || undefined,
    invitedByCode:
      normalized?.invitedByCode == null
        ? undefined
        : String(normalized.invitedByCode),
    accessGrantedAt:
      safeNumber(
        normalized?.accessGrantedAt ??
          normalized?.accessApprovedAt,
        0,
      ) || undefined,
    createdAt:
      safeNumber(
        normalized?.createdAt ??
          normalized?.guestCreatedAt ??
          normalized?.memberNumberAssignedAt,
        0,
      ) || undefined,
    avatar: pickProfileAvatar(normalized),
    avatarUrl: pickProfileAvatar(normalized),
    photoURL: pickProfileAvatar(normalized),
    photoUrl: pickProfileAvatar(normalized),
    profilePhotoUrl: pickProfileAvatar(normalized),
    isAdmin: !!normalized?.isAdmin,
    isSeller: !!normalized?.isSeller,
    xp,
    level: info.level,
    points: xp,
    memberNumber: safeNumber(normalized?.memberNumber, 0) || undefined,
    earlyPro: normalized?.earlyPro === true,
    shells: safeNumber(normalized?.shells, 0),
    foundingMember: normalized?.foundingMember,
    entitlements: normalized?.entitlements,
    reputation: normalized?.reputation,
    // Preserve server-projected public inventory stats for Crew/player cards.
    // These values come from publicProfiles for non-self reads; callers must not
    // rebuild current inventory from progression counters.
    stats: normalized?.stats,
    publicProfileVisibility: normalized?.publicProfileVisibility,
  } as any;
}

export function subscribeToUsers(
  onChange: (users: Record<string, AppUser>) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const colRef = collection(db, 'publicProfiles');
  const q = query(colRef);

  return onSnapshot(
    q,
    (snap) => {
      const next: Record<string, AppUser> = {};
      snap.forEach((d) => {
        next[d.id] = buildUserFromDoc(d.id, d.data());
      });
      onChange(next);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[users] subscribeToUsers publicProfiles error', err);
      onChange({});
    },
  );
}

export async function getUserProfile(userId: string): Promise<AppUser | null> {
  const uid = String(userId || '').trim();
  if (!uid) return null;

  // Full users/{uid} is private to the owner/admin. Other-user identity comes
  // from the server-maintained publicProfiles mirror.
  const isSelf = auth.currentUser?.uid === uid;
  const ref = doc(db, isSelf ? 'users' : 'publicProfiles', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return buildUserFromDoc(uid, snap.data());
}


export async function getCrewMemberProfiles(
  userIds: string[],
): Promise<Record<string, AppUser>> {
  const uids = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    ),
  ).slice(0, 50);

  if (!uids.length) return {};

  await ensureAuthedForWrite('getCrewMemberProfiles');

  const callable = httpsCallable(functions, 'getCrewMemberProfiles');
  const response: any = await callable({ uids });
  const rawProfiles = response?.data?.profiles || {};
  const next: Record<string, AppUser> = {};

  Object.entries(rawProfiles).forEach(([uid, raw]) => {
    next[uid] = buildUserFromDoc(uid, raw || {});
  });

  return next;
}

export function subscribeToUserProfile(
  userId: string,
  onChange: (profile: UserProfile | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const ref = doc(db, 'users', userId);

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onChange(null);
        return;
      }

      const normalized = normalizeProfileLikeShape(userId, snap.data() as any);
      const xp = safeNumber(normalized?.xp ?? normalized?.points ?? 0, 0);
      const info = xpProgress(xp);
      const savedSpots = normalizeSavedSpotsForCompare((normalized as any)?.savedSpots);

      const profile: UserProfile = {
        ...(normalized as any),
        uid: userId,
        id: userId,
        avatar: pickProfileAvatar(normalized),
        avatarUrl: pickProfileAvatar(normalized),
        photoURL: pickProfileAvatar(normalized),
        photoUrl: pickProfileAvatar(normalized),
        profilePhotoUrl: pickProfileAvatar(normalized),
        savedSpots: savedSpots as any,
        xp,
        level: info.level,
        points: xp,
      } as any;

      onChange(profile);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[users] subscribeToUserProfile error', err);
      onChange(null);
    },
  );
}

export function subscribeToPublicUserProfile(
  userId: string,
  onChange: (profile: UserProfile | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const uid = String(userId || '').trim();
  if (!uid) {
    onChange(null);
    return () => {};
  }

  // Public profile screens must never subscribe to another angler's private
  // users/{uid} document. publicProfiles is the server-maintained public mirror.
  const ref = doc(db, 'publicProfiles', uid);

  // Self-heal old/stale public projections when a profile card is actually opened.
  // The callable reads the target's private catch inventory server-side and writes
  // only aggregate publicProfiles stats; no private catches/coordinates are returned.
  void ensureAuthedForWrite('ensurePublicUserProfileProjection')
    .then(async () => {
      if (!auth.currentUser?.uid) return;
      const ensureProjection = httpsCallable(functions, 'ensurePublicUserProfileProjection');
      await ensureProjection({ targetUid: uid });
    })
    .catch((err) => {
      console.warn('[users] public profile projection refresh skipped', { uid, err });
    });

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onChange(null);
        return;
      }

      const normalized = normalizeProfileLikeShape(uid, snap.data() as any);
      const xp = safeNumber(normalized?.xp ?? normalized?.points ?? 0, 0);
      const info = xpProgress(xp);

      onChange({
        ...(normalized as any),
        uid,
        id: uid,
        avatar: pickProfileAvatar(normalized),
        avatarUrl: pickProfileAvatar(normalized),
        photoURL: pickProfileAvatar(normalized),
        photoUrl: pickProfileAvatar(normalized),
        profilePhotoUrl: pickProfileAvatar(normalized),
        xp,
        level: info.level,
        points: xp,
      } as any);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[users] subscribeToPublicUserProfile error', err);
      onChange(null);
    },
  );
}

export async function updateSavedSpots(
  userId: string,
  savedSpotsInput: any[],
): Promise<void> {
  const uid = String(userId || '').trim();
  if (!uid) throw new Error('updateSavedSpots requires a user id');

  const ref = doc(db, 'users', uid);
  const savedSpots = normalizeSavedSpotsForCompare(savedSpotsInput);

  // IMPORTANT: do not pre-read/getDoc here. On Safari/Firestore,
  // getDoc can resolve from cache during save/delete churn. This writer
  // is intentionally unconditional and patch-only: users/{uid}.savedSpots
  // is replaced with the caller 's normalized array every time.
  await ensureAuthedForWrite('updateSavedSpots');
  await setDoc(
    ref,
    stripUndefined({
      savedSpots,
      savedSpotsUpdatedAt: Date.now(),
      updatedAt: Date.now(),
    }) as any,
    { merge: true },
  );
}

export async function updateUserProfile(
  userId: string,
  patch: Partial<UserProfile>,
): Promise<void> {
  const ref = doc(db, 'users', userId);


  const payload: any = { ...(patch as any) };
  // Progression/economy/entitlement state is server-owned. A stale full-profile
  // object must never turn an innocent profile edit into a rejected or unsafe
  // write attempt.
  delete payload.level;
  delete payload.points;
  delete payload.xp;
  delete payload.xpTotal;
  delete payload.shells;
  delete payload.shellBalance;
  delete payload.lifetimeShells;
  delete payload.reputation;
  delete payload.foundingMember;
  delete payload.entitlements;
  // Daily reward state and balances are callable-owned. Stripping them here
  // prevents a stale full-profile save from failing rules or overwriting the
  // server's idempotency data.
  delete payload.dailyRewards;

  const hasIdentityPatch =
    payload.displayName !== undefined ||
    payload.name !== undefined ||
    payload.username !== undefined ||
    payload.handle !== undefined ||
    payload.usernameLower !== undefined ||
    payload.handleLower !== undefined ||
    payload.email !== undefined;

  if (hasIdentityPatch) {
    const normalized = normalizeProfileLikeShape(userId, payload);
    payload.displayName = normalized.displayName;
    payload.name = normalized.name;
    payload.username = normalized.username;
    payload.usernameLower = normalized.usernameLower;
    payload.handle = normalized.handle;
    payload.handleLower = normalized.handleLower;
  }

  const hasAvatarPatch =
    payload.avatar !== undefined ||
    payload.avatarUrl !== undefined ||
    payload.avatarURL !== undefined ||
    payload.photoURL !== undefined ||
    payload.photoUrl !== undefined ||
    payload.imageUrl !== undefined;

  if (hasAvatarPatch) {
    const avatar = pickProfileAvatar(payload);
    if (avatar) {
      payload.avatar = avatar;
      payload.avatarUrl = avatar;
      payload.photoURL = avatar;
      payload.photoUrl = avatar;
      payload.profilePhotoUrl = avatar;
      payload.imageUrl = avatar;
      payload.avatarUpdatedAt = Number(payload.avatarUpdatedAt || Date.now());
    }
  }


  // savedSpots has a dedicated writer so stale full-profile updates cannot
  // accidentally overwrite the map's canonical saved spot array.
  delete payload.savedSpots;
  delete payload.savedSpotsUpdatedAt;
  delete payload.savedSpotsMutationId;

  payload.updatedAt = typeof payload?.updatedAt === 'number' ? payload.updatedAt : Date.now();

  await ensureAuthedForWrite('updateUserProfile');
  await setDoc(ref, stripUndefined(payload) as any, { merge: true });
}

export async function updateNotificationSettings(
  userId: string,
  settings: any,
): Promise<void> {
  await ensureAuthedForWrite('updateNotificationSettings');
  await updateDoc(doc(db, 'users', userId), { notificationSettings: settings });
}

export async function getUserByEmail(email: string): Promise<AppUser | null> {
  const q = query(collection(db, 'publicProfiles'), where('email', '==', email));
  return new Promise((resolve) => {
    const unsub = onSnapshot(
      q,
      (snap) => {
        unsub();
        const d = snap.docs[0];
        if (!d) return resolve(null);
        resolve(buildUserFromDoc(d.id, d.data()));
      },
      () => resolve(null),
    );
  });
}

function buildMentionUserFromDoc(uid: string, data: any) {
  const normalized = normalizeProfileLikeShape(uid, data);
  const username = String(
    normalized?.username ||
    normalized?.handle ||
    normalized?.usernameLower ||
    normalized?.handleLower ||
    '',
  ).trim();

  if (!username) return null;

  return {
    uid,
    username,
    displayName: String(normalized?.displayName || normalized?.name || username),
    avatar: String(
      pickProfileAvatar(normalized) ||
      normalized?.avatarUrl ||
      normalized?.avatarURL ||
      normalized?.photoURL ||
      normalized?.photoUrl ||
      '',
    ),
  };
}

export async function getUserByUsername(rawUsername: string): Promise<{
  uid: string;
  username: string;
  displayName: string;
  avatar?: string;
} | null> {
  const normalized = normalizeHandle(rawUsername).replace(/^@+/, '');
  if (!normalized) return null;

  const usersCol = collection(db, 'publicProfiles');

  const candidateQueries = [
    query(usersCol, where('usernameLower', '==', normalized)),
    query(usersCol, where('handleLower', '==', normalized)),
    query(usersCol, where('username', '==', normalized)),
    query(usersCol, where('handle', '==', normalized)),
  ];

  for (const q of candidateQueries) {
    try {
      const snap = await getDocs(q);
      const first = snap.docs[0];
      if (first) {
        const hit = buildMentionUserFromDoc(first.id, first.data());
        if (hit) return hit;
      }
    } catch {}
  }


  return null;
}


// ------------------------------------------------------------
// Crew / relationship foundation
// ------------------------------------------------------------

function relationshipDocId(a: string, b: string) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  return [left, right].sort().join('__');
}

function relationshipRef(a: string, b: string) {
  return doc(db, 'relationships', relationshipDocId(a, b));
}

function nowMs() {
  return Date.now();
}

function normalizeRelationshipDoc(id: string, data: any): UserRelationship {
  const requesterId = String(data?.requesterId || '');
  const recipientId = String(data?.recipientId || '');
  const participantIds = Array.isArray(data?.participantIds)
    ? data.participantIds.map(String).filter(Boolean)
    : [requesterId, recipientId].filter(Boolean).sort();

  return {
    id,
    participantIds,
    requesterId,
    recipientId,
    status: String(data?.status || 'pending') as any,
    createdAt: safeNumber(data?.createdAt, 0),
    updatedAt: safeNumber(data?.updatedAt, 0),
    acceptedAt: data?.acceptedAt === undefined ? undefined : safeNumber(data?.acceptedAt, 0),
    declinedAt: data?.declinedAt === undefined ? undefined : safeNumber(data?.declinedAt, 0),
    removedAt: data?.removedAt === undefined ? undefined : safeNumber(data?.removedAt, 0),
    removedBy: data?.removedBy ? String(data.removedBy) : undefined,
    blockedAt: data?.blockedAt === undefined ? undefined : safeNumber(data?.blockedAt, 0),
    blockedBy: data?.blockedBy ? String(data.blockedBy) : undefined,
  };
}

export function getRelationshipDocId(a: string, b: string) {
  return relationshipDocId(a, b);
}

export function getRelationshipState(currentUid: string, targetUid: string, rel?: UserRelationship | null): SocialRelationshipStatus {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) return 'none';
  if (!rel) return 'none';
  if (rel.status === 'accepted') return 'friends';
  if (rel.status === 'pending') return rel.requesterId === current ? 'pending_sent' : 'pending_received';
  if (rel.status === 'blocked') {
    if (rel.blockedBy === current) return 'blocked_by_me';
    if (rel.blockedBy === target) return 'blocked_me';
    return 'blocked';
  }
  return rel.status as SocialRelationshipStatus;
}

export function subscribeToRelationshipBetween(
  currentUid: string,
  targetUid: string,
  onChange: (relationship: UserRelationship | null, state: SocialRelationshipStatus) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) {
    onChange(null, 'none');
    return () => {};
  }

  return onSnapshot(
    relationshipRef(current, target),
    (snap) => {
      if (!snap.exists()) {
        onChange(null, 'none');
        return;
      }
      const rel = normalizeRelationshipDoc(snap.id, snap.data());
      onChange(rel, getRelationshipState(current, target, rel));
    },
    (err) => {
      console.warn('[users] relationship subscription failed', err);
      onError?.(err);
      onChange(null, 'none');
    },
  );
}

export function subscribeToCrewRelationships(
  uid: string,
  onChange: (relationships: UserRelationship[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const current = String(uid || '').trim();
  if (!current) {
    onChange([]);
    return () => {};
  }

  let cancelled = false;
  const unsubs: Unsubscribe[] = [];
  const buckets = new Map<string, UserRelationship[]>();

  const emit = () => {
    if (cancelled) return;

    const merged = new Map<string, UserRelationship>();

    Array.from(buckets.values()).flat().forEach((rel) => {
      const participantIds = Array.isArray(rel.participantIds) ? rel.participantIds : [];
      const touchesCurrent =
        participantIds.includes(current) ||
        rel.requesterId === current ||
        rel.recipientId === current;

      if (!touchesCurrent) return;

      merged.set(rel.id, {
        ...rel,
        participantIds: participantIds.length
          ? participantIds
          : [rel.requesterId, rel.recipientId].filter(Boolean).sort(),
      });
    });

    onChange(
      Array.from(merged.values()).sort(
        (a, b) => safeNumber(b.updatedAt || b.createdAt, 0) - safeNumber(a.updatedAt || a.createdAt, 0),
      ),
    );
  };

  const attach = (key: string, q: ReturnType<typeof query>, required = false) => {
    const unsub = onSnapshot(
      q,
      (snap) => {
        buckets.set(
          key,
          snap.docs.map((d) => normalizeRelationshipDoc(d.id, d.data())),
        );
        emit();
      },
      (err) => {
        // Required means the canonical participantIds listener failed.
        // Legacy listeners are still useful for older relationship docs, but
        // should not knock the UI offline by themselves.
        console.warn(`[users] crew relationships ${key} listener failed`, err);
        buckets.set(key, []);
        if (required) onError?.(err);
        emit();
      },
    );
    unsubs.push(unsub);
  };

  const boot = async () => {
    // Wait for auth bridge so Firestore rules see request.auth.
    await ensureAuthedForWrite('subscribeToCrewRelationships');
    if (cancelled) return;

    // Use the two owner-side equality queries as the stable source of truth.
    // Every relationship doc Roddo creates has requesterId + recipientId, including
    // docs that also have participantIds. The participantIds array query is useful
    // for future schema-only reads, but it is the query currently throwing
    // permission noise in production for mixed legacy data, so do not attach it here.
    attach('requesterId', query(collection(db, 'relationships'), where('requesterId', '==', current)), true);
    attach('recipientId', query(collection(db, 'relationships'), where('recipientId', '==', current)), true);
  };

  boot().catch((err) => {
    console.warn('[users] crew relationship listener attach failed', err);
    onError?.(err);
    onChange([]);
  });

  return () => {
    cancelled = true;
    unsubs.forEach((unsub) => {
      try {
        unsub();
      } catch {}
    });
  };
}

async function getNotificationActorForUser(uid: string): Promise<NotificationActor> {
  const cleanUid = String(uid || '').trim();
  if (!cleanUid) return { uid: '' };
  try {
    const snap = await getDoc(doc(db, 'publicProfiles', cleanUid));
    const data = snap.exists() ? (snap.data() as any) : {};
    return {
      uid: cleanUid,
      name: String(data?.name || data?.displayName || data?.username || data?.handle || 'Someone'),
      avatar: String(data?.avatar || data?.avatarUrl || data?.photoURL || ''),
    };
  } catch {
    return { uid: cleanUid, name: 'Someone' };
  }
}

async function createCrewRequestNotification(requesterUid: string, recipientUid: string) {
  const actor = await getNotificationActorForUser(requesterUid);
  const name = actor.name || 'Someone';
  await createNotification({
    userId: recipientUid,
    id: `crew_request_${relationshipDocId(requesterUid, recipientUid)}_${nowMs()}`,
    type: 'FRIEND_REQUEST' as NotificationType,
    title: 'Crew request',
    body: `${name} wants to join your crew.`,
    message: `${name} wants to join your crew.`,
    actor,
    entity: { userId: requesterUid },
    link: { view: View.PublicProfile, targetId: requesterUid },
    read: false,
  } as any).catch((err) => console.warn('[users] crew request notification skipped', err));
}

async function createCrewAcceptedNotification(accepterUid: string, requesterUid: string) {
  const actor = await getNotificationActorForUser(accepterUid);
  const name = actor.name || 'Someone';
  await createNotification({
    userId: requesterUid,
    id: `crew_accepted_${relationshipDocId(accepterUid, requesterUid)}_${nowMs()}`,
    type: 'FRIEND_ACCEPTED' as NotificationType,
    title: 'Crew confirmed',
    body: `You and ${name} are now crew.`,
    message: `You and ${name} are now crew.`,
    actor,
    entity: { userId: accepterUid },
    link: { view: View.PublicProfile, targetId: accepterUid },
    read: false,
  } as any).catch((err) => console.warn('[users] crew accepted notification skipped', err));
}

export async function sendFriendRequest(currentUid: string, targetUid: string): Promise<void> {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) throw new Error('Invalid crew target');
  await ensureAuthedForWrite('sendFriendRequest');
  const ref = relationshipRef(current, target);
  const existing = await getDoc(ref);
  const now = nowMs();
  if (existing.exists()) {
    const rel = normalizeRelationshipDoc(existing.id, existing.data());
    if (rel.status === 'accepted') return;
    if (rel.status === 'blocked') throw new Error('This relationship is blocked.');
  }
  await setDoc(ref, {
    participantIds: [current, target].sort(),
    requesterId: current,
    recipientId: target,
    status: 'pending',
    createdAt: existing.exists() ? safeNumber((existing.data() as any)?.createdAt, now) : now,
    updatedAt: now,
  }, { merge: true });
  await createCrewRequestNotification(current, target);
}

export async function cancelFriendRequest(currentUid: string, targetUid: string): Promise<void> {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) throw new Error('Invalid crew target');
  await ensureAuthedForWrite('cancelFriendRequest');
  const now = nowMs();
  await updateDoc(relationshipRef(current, target), {
    participantIds: [current, target].sort(),
    status: 'removed',
    removedAt: now,
    removedBy: current,
    updatedAt: now,
  });
}

export async function acceptFriendRequest(currentUid: string, requesterUid: string): Promise<void> {
  const current = String(currentUid || '').trim();
  const requester = String(requesterUid || '').trim();
  if (!current || !requester || current === requester) throw new Error('Invalid crew request');
  await ensureAuthedForWrite('acceptFriendRequest');
  const now = nowMs();
  await updateDoc(relationshipRef(current, requester), {
    participantIds: [current, requester].sort(),
    status: 'accepted',
    acceptedAt: now,
    updatedAt: now,
  });
  await createCrewAcceptedNotification(current, requester);
}

export async function declineFriendRequest(currentUid: string, requesterUid: string): Promise<void> {
  const current = String(currentUid || '').trim();
  const requester = String(requesterUid || '').trim();
  if (!current || !requester || current === requester) throw new Error('Invalid crew request');
  await ensureAuthedForWrite('declineFriendRequest');
  const now = nowMs();
  await updateDoc(relationshipRef(current, requester), {
    participantIds: [current, requester].sort(),
    status: 'declined',
    declinedAt: now,
    updatedAt: now,
  });
}

export async function removeFriend(currentUid: string, targetUid: string): Promise<void> {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) throw new Error('Invalid crew target');
  await ensureAuthedForWrite('removeFriend');
  const now = nowMs();
  await updateDoc(relationshipRef(current, target), {
    participantIds: [current, target].sort(),
    status: 'removed',
    removedAt: now,
    removedBy: current,
    updatedAt: now,
  });
}

export async function blockUser(currentUid: string, targetUid: string, reason = ''): Promise<void> {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) throw new Error('Invalid block target');
  await ensureAuthedForWrite('blockUser');
  const now = nowMs();
  await setDoc(doc(db, 'users', current, 'blockedUsers', target), stripUndefined({
    blockerUid: current,
    blockedUid: target,
    reason: String(reason || '').trim(),
    createdAt: now,
  }) as any, { merge: true });
  await setDoc(relationshipRef(current, target), {
    participantIds: [current, target].sort(),
    requesterId: current,
    recipientId: target,
    status: 'blocked',
    blockedBy: current,
    blockedAt: now,
    updatedAt: now,
    createdAt: now,
  }, { merge: true });
}

export async function unblockUser(currentUid: string, targetUid: string): Promise<void> {
  const current = String(currentUid || '').trim();
  const target = String(targetUid || '').trim();
  if (!current || !target || current === target) throw new Error('Invalid unblock target');
  await ensureAuthedForWrite('unblockUser');
  await deleteDoc(doc(db, 'users', current, 'blockedUsers', target));
  const now = nowMs();
  await updateDoc(relationshipRef(current, target), {
    participantIds: [current, target].sort(),
    status: 'removed',
    removedAt: now,
    removedBy: current,
    updatedAt: now,
  });
}
